import React, { ReactNode, useEffect, useState } from "react";

interface Step {
  label?: string;
  content: ReactNode;
  validator: () => boolean;
  path?: string;
  condition?: () => boolean;
  onClick?: () => Promise<boolean>;
  ctaText?: string;
}

interface HorizontalStepperProps {
  steps: Step[];
  currentStep?: number;
  completedSteps?: boolean[];
}

const HorizontalStepper: React.FC<HorizontalStepperProps> = ({
  steps,
  currentStep: externalCurrentStep,
  completedSteps: externalCompletedSteps,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(
    externalCurrentStep ? externalCurrentStep - 1 : 0
  );
  const [completedSteps, setCompletedSteps] = useState<boolean[]>(
    externalCompletedSteps || new Array(steps.length).fill(false)
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (externalCurrentStep !== undefined) {
      setCurrentStepIndex(externalCurrentStep - 1);
    }
  }, [externalCurrentStep]);

  useEffect(() => {
    if (externalCompletedSteps) {
      setCompletedSteps(externalCompletedSteps);
    } else {
      const newCompletedSteps = new Array(steps.length).fill(false);
      if (externalCurrentStep && externalCurrentStep > 1) {
        for (let i = 0; i < externalCurrentStep - 1; i++) {
          newCompletedSteps[i] = true;
        }
      }
      setCompletedSteps(newCompletedSteps);
    }
  }, [externalCurrentStep, externalCompletedSteps, steps.length]);

  useEffect(() => {
    if (!externalCurrentStep) {
      setCurrentStepIndex(0);
      setCompletedSteps(new Array(steps.length).fill(false));
    }
  }, [steps.length, externalCurrentStep]);

  const filteredSteps = steps.filter((step) =>
    step.condition ? step.condition() : true
  );
  const currentStep = filteredSteps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;

  const nextStep = async () => {
    if (currentStepIndex < filteredSteps.length) {
      if (currentStep?.validator && currentStep.validator()) {
        setLoading(true);

        let proceed = true;
        if (currentStep.onClick) {
          proceed = await currentStep.onClick();
        }

        setLoading(false);
        if (proceed && currentStepIndex + 1 < filteredSteps.length) {
          const updatedCompletedSteps = [...completedSteps];
          updatedCompletedSteps[currentStepIndex] = true;
          setCompletedSteps(updatedCompletedSteps);
          setCurrentStepIndex(currentStepIndex + 1);
        }
      }
    }
  };

  const previousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  return (
    <div className="flex flex-col space-y-[20px] pb-20 min-h-[calc(100vh-128px)]">
      <div className="flex items-center justify-start w-full">
        {filteredSteps.map((step, index) => {
          const isStepCompleted = completedSteps[index];
          const isCurrentStep = index === currentStepIndex;
          const isDisabledStep = index > currentStepIndex;

          let numberBgColor, numberTextColor;

          if (isCurrentStep) {
            numberBgColor = "bg-bg-page-body";
            numberTextColor = "text-text-button-primary";
          } else if (isStepCompleted) {
            numberBgColor = "bg-bg-button-primary";
            numberTextColor = "text-text-base";
          } else if (isDisabledStep) {
            numberBgColor = "bg-bg-page-body";
            numberTextColor = "text-text-base";
          }

          return (
            <React.Fragment key={index}>
              <div className="flex flex-col items-center">
                <div
                  className={`rounded-full w-[24px] h-[24px] flex items-center justify-center ${numberBgColor} ${isCurrentStep ? "border border-border-button-primary" : ""
                    }`}
                >
                  <span
                    className={`text-[12px] font-semibold ${numberTextColor}`}
                  >
                    {index + 1}
                  </span>
                </div>
              </div>
              {index < filteredSteps.length - 1 && (
                <div
                  className={`flex-1 h-[2px] ${isStepCompleted ? "bg-bg-button-primary" : "bg-bg-disabled"
                    }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div>
        {currentStep.label && (
          <div className="text-[12px] font-medium rounded-lg bg-bg-base text-text-base mb-[8px]">
            {currentStep.label}
          </div>
        )}

        <div>{currentStep.content}</div>
      </div>

      <div className="flex justify-start gap-3 max-w-full">
        {!isFirstStep && (
          <button
            onClick={previousStep}
            disabled={isFirstStep || loading}
            className="
                flex items-center justify-center gap-2
                px-6 py-3
                h-10
                flex-1
                text-text-button-tertiary border border-text-button-tertiary bg-transparent rounded-lg
                disabled:cursor-not-allowed
                text-[14px]
              "
          >
            BACK
          </button>
        )}
        {currentStep.ctaText && (
          <button
            onClick={nextStep}
            disabled={!currentStep.validator() || loading}
            className={`
                flex items-center justify-center gap-2
                px-6 py-3
                h-10
                flex-1
                text-[14px]
                text-text-base font-medium rounded-lg bg-bg-button-primary disabled:bg-bg-disabled disabled:text-text-disabled disabled:cursor-not-allowed
              `}
          >
            {currentStep.ctaText}
          </button>
        )}
      </div>
    </div>
  );
};

export default HorizontalStepper;
